import type { PartDefinition } from "@rogue/rackbuilder"
import { getRackModel } from "../../modelPaths"
import { StorageChainConfig, MassStorageOptions } from "../../RackTypes"
import { MsShelfData } from "./ms-hardware-counting-utils"
import { findBundleProduct } from "../rackbuilder-util"
import { IRogue, BuyRequest } from "@/js/services/RogueTypes"
import { getHangerSelection, getShelfSelection } from "../selection-utils"

const HANGER_SLOTS_BY_QUANTITY: { [quantity: number]: string[] } = {
  2 : ['one', 'four'],
  4 : ['one', 'two', 'three', 'four'],
}

function createHangerSlots(hangerQuantity: string | undefined): { [key: string]: PartDefinition } {
  const slots: { [key: string]: PartDefinition } = {}
  const quantity = Number(hangerQuantity) || 0
  const slotNames = HANGER_SLOTS_BY_QUANTITY[quantity] || []

  slotNames.forEach(slotName => {
    slots[slotName] = {
      mesh  : getRackModel('PARTS', 'hanger'),
      slots : {},
    }
  })

  return slots
}

function createShelfPart(selection: string | undefined, width: string, hangerQuantity?: string, yOffset = 0): PartDefinition[] {
  let shelf: PartDefinition[] = []

  if (selection && width) {
    shelf = [{
      mesh   : getRackModel('PARTS', `${selection} ${width}`),
      slots  : selection === 'rail' ? createHangerSlots(hangerQuantity) : {},
      offset : yOffset ? {x: 0.1, y: yOffset} : {x: 0.1},
    }]
  }

  return shelf
}

export function extractNamedShelfSelections(rogue: IRogue, buyRequest: BuyRequest): Record<string, string|undefined> {
  const result: Record<string, string|undefined> = {}
  const names = ['One', 'Two', 'Three', 'Four', 'Five']

  for (let i = 0; i < names.length; i += 1) {
    const name = names[i]
    const index  = i + 1

    const corner = findBundleProduct(rogue, buyRequest, 'corner', true)?.name

    if (corner) {
      result[`shelf${name}Selection`] = 'corner'
    } else {
      const shelfBundle = findBundleProduct(rogue, buyRequest, `shelf ${index}`, true)
      const shelfSelection = getShelfSelection(shelfBundle?.name)

      result[`shelf${name}Selection`] = shelfSelection
      // Only a 'rail' shelf can carry a hanger config — never parse pack-count text off a non-rail product name.
      result[`shelf${name}HangerQuantity`] = shelfSelection === 'rail' ? getHangerSelection(shelfBundle?.name) : '0'
    }
  }

  return result
}

export function getShelfSpacing(height: string, shelfId: string): number {
  // 43" units get a flat 16" spacing on every shelf
  if (height === '43') return 16

  // 70" units: 20" spacing everywhere except a 16" gap up to the top shelf.
  // A shelf's spacing reserves the space ABOVE it, so the 16" lives on shelf three
  // (the shelf below the top) — not on shelf four, which has nothing above it.
  if (height === '70') return shelfId === 'shelf_three' ? 16 : 20

  // All other heights keep the standard 20" spacing
  return 20
}

export function getMaxZByHeight(height: string): number {
  // These values should match the maximum allowed height per rack configuration
  if (height === '70') return 88

  return 66
}

export function isPositionBlocked(startZ: number, height: number, occupiedRanges: Array<{start: number, end: number}>): boolean {
  const endZ = startZ + height

  return occupiedRanges.some(range => (startZ < range.end && endZ > range.start))
}

// Vertical offset (inches, positive = up) for a rail crossmember on each shelf of a
// 43" unit. Tune per shelf as needed; shelves not listed here get no offset.
const RAIL_Y_OFFSET_43: Record<string, number> = {
  shelf_one   : 0,
  shelf_two   : 0,
  shelf_three : -4,
}

export function assignShelfPlacement(shelfTarget: PartDefinition, shelfConfig: StorageChainConfig, config: MassStorageOptions) {
  const shelfConfigs = [
    { id: 'shelf_one', selection: shelfConfig.shelfOneSelection, hangerQuantity: shelfConfig.shelfOneHangerQuantity, condition: true, range: [0, 0] },
    { id: 'shelf_two', selection: shelfConfig.shelfTwoSelection, hangerQuantity: shelfConfig.shelfTwoHangerQuantity, condition: true, range: [16, 30] },
    { id: 'shelf_three', selection: shelfConfig.shelfThreeSelection, hangerQuantity: shelfConfig.shelfThreeHangerQuantity, condition: true, range: [32, 48] },
    { id: 'shelf_four', selection: shelfConfig.shelfFourSelection, hangerQuantity: shelfConfig.shelfFourHangerQuantity, condition: shelfConfig.heightSelection === '70', range: [48, 62] },
  ]

  const maxZ = getMaxZByHeight(shelfConfig.heightSelection)
  const unitShelves: MsShelfData[] = []

  // Keep track of occupied positions
  const occupiedRanges: Array<{start: number, end: number}> = []

  shelfConfigs.forEach(({ id, selection, hangerQuantity, condition, range }) => {
    if (!condition || !selection) return

    // The rail crossmember is not offered on 70"-wide units
    const effectiveWidth = shelfConfig.widthSelection || config.width
    if (selection === 'rail' && effectiveWidth === '70') return

    // The rail crossmember is not offered on shelf 3 for a 43" unit when shelf 2 is a dumbbell shelf
    const isDumbbellIn2 = shelfConfig.shelfTwoSelection?.toLowerCase().includes('dumbbell')
    if (selection === 'rail' && id === 'shelf_three' && shelfConfig.heightSelection === '43' && isDumbbellIn2) return

    const shelfHeight = getShelfSpacing(shelfConfig.heightSelection, id)
    let targetZ = range[0] // Start with preferred position

    // If the preferred position is blocked, find the lowest available position
    while (isPositionBlocked(targetZ, shelfHeight, occupiedRanges) && targetZ <= maxZ) {
      targetZ += 1 // Move up by 1 unit until we find space
    }

    const slotName = `${id}_${targetZ}h`
    const is43Height = shelfConfig.heightSelection === '43'
    const is70Height = shelfConfig.heightSelection === '70'
    const shelfType = getShelfSelection(selection)

    // Prevent placing this shelf if it would exceed the total allowed height unless it's the last shelf on the 43" and 70" unit heights
    if (targetZ + shelfHeight > maxZ && !(is43Height && slotName.includes('shelf_three') || is70Height && slotName.includes('shelf_four'))) {
      return
    } 
    
    occupiedRanges.push({ start: targetZ, end: targetZ + shelfHeight })

    // Rails on a 43" unit get a per-shelf vertical nudge to line up with the frame (see RAIL_Y_OFFSET_43).
    const yOffset = selection === 'rail' && is43Height ? (RAIL_Y_OFFSET_43[id] ?? 0) : 0

    unitShelves.push({ hardwareSlotName: slotName, shelfType })
    shelfTarget.slots[slotName] = createShelfPart(selection, shelfConfig.widthSelection || config.width, hangerQuantity, yOffset)
  })

  return unitShelves
}
